"use client";

import { PageBottomCta } from "@/components/PageBottomCta";
import { useLanguage } from "@/components/LanguageProvider";
import { resolveApiAssetUrl } from "@/lib/backend";
import type { PageSection } from "@/lib/api";
import { stripHtml } from "@/lib/utils";
import type { Product } from "@/lib/types";
import Image from "next/image";
import Link from "next/link";

function firstLogo(logos: string | undefined): string | null {
  if (!logos?.trim()) return null;
  return logos.split(",")[0]?.trim() ?? null;
}

type Props = { products: Product[]; cmsCta?: PageSection };

export function ProductsContent({ products, cmsCta }: Props) {
  const { t, dir, lang } = useLanguage();

  return (
    <div dir={dir}>
      <header className="relative overflow-hidden bg-gradient-to-br from-[var(--primary-darker)] via-[var(--primary)] to-[var(--primary-dark)] px-4 py-16 text-center text-white md:py-24">
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--accent)]">
          {t("products.badge")}
        </p>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tight">
          {t("products.title")}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-slate-300">
          {t("products.subtitle")}
        </p>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {products.length === 0 ? (
            <p className="col-span-full rounded-2xl border border-dashed border-slate-300 py-16 text-center text-slate-500">
              {t("products.empty")}
            </p>
          ) : (
            products.map((p) => {
              const logo = firstLogo(p.logos);
              const logoSrc = resolveApiAssetUrl(logo);
              const excerpt = stripHtml(p.content ?? "").slice(0, 180);
              return (
                <Link
                  key={p.id}
                  href={`/products/${p.slug}`}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:border-[var(--accent)]/40 hover:shadow-xl"
                >
                  <div className="flex min-h-[140px] items-center justify-center bg-gradient-to-b from-slate-100 to-slate-50 px-6 py-8">
                    {logoSrc ? (
                      <Image
                        src={logoSrc}
                        alt=""
                        width={200}
                        height={80}
                        className="max-h-16 w-auto object-contain"
                        unoptimized
                      />
                    ) : (
                      <span className="text-4xl opacity-40" aria-hidden>📦</span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h2 className="text-lg font-bold text-[var(--primary)] group-hover:text-[var(--accent)]">
                      {lang === "ar" && p.nameAr ? p.nameAr : p.name}
                    </h2>
                    <p className="mt-2 line-clamp-3 flex-1 text-sm text-slate-600">
                      {excerpt}{excerpt.length >= 180 ? "…" : ""}
                    </p>
                  </div>
                  <div className="border-t border-slate-100 bg-[var(--accent)]/5 px-6 py-3 text-sm font-semibold text-[var(--accent-foreground)]">
                    {t("products.viewDetails")}
                  </div>
                </Link>
              );
            })
          )}
        </div>
      </div>

      <PageBottomCta
        cmsCta={cmsCta}
        fallback={{
          heading: "products.cta.heading",
          subheading: "products.cta.subtitle",
          button: "products.cta.button",
        }}
      />
    </div>
  );
}
